import { useApplicantAPI, useRequest } from "@certn/api";
import { showToast } from "@certn/cascadia";
import {
  ActionList,
  ActionListItem,
  BasicButton,
  Link,
  PrimaryButton,
  SectionMessage,
  Stack,
  SummaryCard,
  Text,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useState } from "react";
import { useTranslation, Trans } from "react-i18next";

import { useApplication } from "./ApplicationContext";
import { useApplicationFormContext } from "./ApplicationFormContext";

export function OneIdDiscrepancyCheck() {
  const { completedSections, basePath = "apply" } = useApplication();
  const { caseId } = useApplicationFormContext();
  const { data, isLoading } = useApplicantAPI(
    `/api/internal/applicant/case/${caseId}/one-id/request-verified-data/`
  );
  const { mutate } = useApplicantAPI(`/api/internal/applicant/case/${caseId}/`);
  const request = useRequest();
  const { t } = useTranslation();
  const [disableExtractedDetailsButton, setDisableExtractedDetailsButton] =
    useState(false);
  const [useExistingDetailsButton, setUseExistingDetailsButton] =
    useState(true);
  const [useExtractedDetailsButton, setUseExtractedDetailsButton] =
    useState(true);
  const editNameLink = Boolean(
    completedSections?.includes("personal-information")
  ) && (
    <Link
      textStyle="global/body-secondary"
      textColor="text-weak"
      href={`/${basePath}/${caseId}/personal-information`}
      testID="dispute-report-link-personal-information"
    >
      <Trans>Edit</Trans>
    </Link>
  );

  return (
    <>
      {data?.has_discrepancy && (
        <SummaryCard
          header={t("ID Verification")}
          testID="discrepancy-section-card"
        >
          <SectionMessage
            noBorderRadius
            icon="verified_user"
            sectionTitle={t("Information discrepancy")}
            type="danger"
          >
            <Trans>
              We noticed a discrepancy between the information we read from your
              government ID and the information you provided on the Personal
              Information section. Please review and confirm the information
              that matches your government ID. If you elect to submit details
              which differ from the information read from your ID, the
              discrepancy will be reported on your background check report.
            </Trans>
          </SectionMessage>
          <Stack orientation="horizontal">
            <SummaryCard
              noBorder
              noHeaderBorder
              header={t("You entered:")}
              className={styles.summaryCard}
              testID="applicant-provided-information"
            >
              <ActionList hideBorder>
                <ActionListItem
                  title={t("Given name")}
                  trailing={editNameLink}
                  testID="first-name-label"
                >
                  <Text style="global/body-secondary" color="text-weak">
                    {String(data.applicant_name.given_name)}
                  </Text>
                </ActionListItem>
                {data.applicant_name.additional_name ? (
                  <ActionListItem
                    title={t("Additional given name (if applicable)")}
                    trailing={editNameLink}
                    testID="middle-name-label"
                  >
                    <Text style="global/body-secondary" color="text-weak">
                      {String(data.applicant_name.additional_name)}
                    </Text>
                  </ActionListItem>
                ) : data.verified_name.additional_name ? (
                  <ActionListItem
                    title={t("Additional given name (if applicable)")}
                  >
                    <Text color="text-disabled" style="global/body-secondary">
                      Not Applicable
                    </Text>
                  </ActionListItem>
                ) : null}
                <ActionListItem
                  title={t("Family name")}
                  trailing={editNameLink}
                  testID="last-name-label"
                >
                  <Text style="global/body-secondary" color="text-weak">
                    {String(data.applicant_name.family_name)}
                  </Text>
                </ActionListItem>
                <ActionListItem
                  title={t("Date of birth")}
                  testID="dob-label"
                  trailing={
                    Boolean(completedSections?.includes("birth-details")) && (
                      <Link
                        textStyle="global/body-secondary"
                        textColor="text-weak"
                        href={`/${basePath}/${caseId}/birth-details`}
                        testID="dispute-report-link-personal-information"
                      >
                        <Trans>Edit</Trans>
                      </Link>
                    )
                  }
                >
                  <Text style="global/body-secondary" color="text-weak">
                    {data.applicant_date_of_birth}
                  </Text>
                </ActionListItem>
                {useExistingDetailsButton && (
                  <ActionListItem>
                    <BasicButton
                      isDisabled={isLoading || disableExtractedDetailsButton}
                      testID="details-match-button"
                      onPress={() => {
                        setUseExistingDetailsButton(false);
                        setUseExtractedDetailsButton(false);
                      }}
                    >
                      <Trans>These details match my ID</Trans>
                    </BasicButton>
                  </ActionListItem>
                )}
              </ActionList>
            </SummaryCard>
            <SummaryCard
              noBorder
              noHeaderBorder
              header={t("Your ID says:")}
              className={styles.summaryCard}
              testID="extracted-information"
            >
              <ActionList hideBorder>
                <ActionListItem title={t("Given name")}>
                  <Text
                    style="global/body-secondary"
                    color="text-weak"
                    testID="first-name-content"
                  >
                    {String(data.verified_name.given_name)}
                  </Text>
                </ActionListItem>
                {data.verified_name.additional_name ? (
                  <ActionListItem
                    title={t("Additional given name (if applicable)")}
                  >
                    <Text
                      style="global/body-secondary"
                      color="text-weak"
                      testID="middle-name-content"
                    >
                      {String(data.verified_name.additional_name)}
                    </Text>
                  </ActionListItem>
                ) : data.applicant_name.additional_name ? (
                  <ActionListItem
                    title={t("Additional given name (if applicable)")}
                  >
                    <Text color="text-disabled" style="global/body-secondary">
                      Not Applicable
                    </Text>
                  </ActionListItem>
                ) : null}
                <ActionListItem title={t("Family name")}>
                  <Text
                    style="global/body-secondary"
                    color="text-weak"
                    testID="last-name-content"
                  >
                    {String(data.verified_name.family_name)}
                  </Text>
                </ActionListItem>
                <ActionListItem title={t("Date of birth")}>
                  <Text
                    style="global/body-secondary"
                    color="text-weak"
                    testID="dob-content"
                  >
                    {data.verified_date_of_birth}
                  </Text>
                </ActionListItem>
                {useExtractedDetailsButton && (
                  <ActionListItem>
                    <PrimaryButton
                      testID="use-extracted-id-button"
                      isDisabled={isLoading || disableExtractedDetailsButton}
                      onPress={async () => {
                        try {
                          setDisableExtractedDetailsButton(true);
                          await request(
                            `/api/internal/applicant/case/${caseId}/one-id/accept-discrepancy/`,
                            {
                              method: "POST",
                            }
                          );
                          await mutate();
                          setDisableExtractedDetailsButton(false);
                          setUseExistingDetailsButton(false);
                          setUseExtractedDetailsButton(false);
                        } catch (e) {
                          showToast(
                            t(
                              "Something went wrong updating your one id details."
                            ),
                            {
                              type: "error",
                            }
                          );
                          throw e;
                        }
                      }}
                    >
                      <Trans>Use ID extracted details</Trans>
                    </PrimaryButton>
                  </ActionListItem>
                )}
              </ActionList>
            </SummaryCard>
          </Stack>
        </SummaryCard>
      )}
    </>
  );
}

const styles = {
  summaryCard: css({
    flex: 1,
  }),
};
